import React from "react";
import { useNavigate } from "react-router-dom";
import "./VotingRules.css";

function VotingRule() {
  const navigate = useNavigate();

  return (
    <div className="VotingRule">
      <div className="privacyHeader">
        <img
          src="/Assets/Header/amalgamateLogo.svg"
          alt="AmalgaMate Logo"
          className="privacyLogo"
          onClick={() => navigate("/amalgamate")}
        />
        <span className="privacyHeaderText">VOTING & COMMUNITY RULES</span>
      </div>

      <div className="privacyPolicyContainer">
        <div className="privacyPolicyContainer2">
          <span className="lastUpdatedText">Effective Date: October 2026 | Last Updated: October 2026</span>

          <div className="highlightBox">
            <p>
              <strong>Core Mission:</strong> The AmalgaMate platform provides direct, transparent collective decision-making tools to unite communities and discover shared consensus. Every ballot, reaction, and verified vote empowers humanity to collaborate on solutions for real-world issues.
            </p>
          </div>

          {/* Section 1 */}
          <div className="legalSection">
            <span className="smallHeaderText">1. Community Code of Conduct</span>
            <p className="smallText">
              Our platform thrives on authentic, respectful, and constructive engagement. By participating in AmalgaMate, you agree to:
            </p>
            <ul className="legalList">
              <li><strong>Support the Mission:</strong> Align your contributions with our goal of uniting communities, fostering transparency, and finding common ground.</li>
              <li><strong>Promote Truth & Authenticity:</strong> Share truthful, accurate information. Inauthentic data, intentional misinformation, and coordinated manipulation undermine collective decision-making.</li>
              <li><strong>Civil & Respectful Engagement:</strong> Treat all members with dignity. Harassment, hate speech, bullying, discrimination, and targeted personal attacks are strictly prohibited.</li>
              <li><strong>Constructive Deliberation:</strong> Contribute meaningful perspectives, thoughtful critiques, and actionable solutions rather than disruptive or bad-faith arguments.</li>
              <li><strong>Value Verified Knowledge:</strong> Enhance discussions through demonstrated knowledge, completed learning modules, and verified certifications.</li>
            </ul>
          </div>

          {/* Section 2 */}
          <div className="legalSection">
            <span className="smallHeaderText">2. Core Voting Principles</span>
            <p className="smallText">
              AmalgaMate provides a direct democratic framework designed to empower individual voices on community topics and global challenges:
            </p>
            <ul className="legalList">
              <li><strong>One Verified Human, One Vote:</strong> Every verified user is granted exactly one vote per ballot. Identity and face verification ensure genuine human participation and prevent duplicate voting or automated bot manipulation.</li>
              <li><strong>Dynamic, Changeable Votes:</strong> You can update or change your vote on any active ballot at any time prior to ballot closure as new discussions, evidence, or perspectives emerge.</li>
              <li><strong>Transparent Vote Distribution:</strong> Aggregate voting results, option percentages, and participation statistics are displayed openly so participants can clearly see where collective alignment lies.</li>
              <li><strong>Individual Vote Privacy:</strong> Individual ballot choices remain private by default, enabling voters to express their genuine convictions freely.</li>
              <li><strong>Discussion & Feedback:</strong> Users can discuss issues directly on ballots through comments, like/dislike reactions, and thoughtful civic deliberation.</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="legalSection">
            <span className="smallHeaderText">3. Sub-Ballots & Multi-Layer Deliberation</span>
            <p className="smallText">
              Complex problems often require more than simple high-level choices. AmalgaMate supports nested decision-making through sub-ballots:
            </p>
            <ul className="legalList">
              <li><strong>Deep-Dive Sub-Ballots:</strong> When an option within a ballot warrants detailed exploration, a linked sub-ballot can be attached to that option to explore specific strategies, nuances, or implementation approaches.</li>
              <li><strong>Granular Decision-Making:</strong> Sub-ballots enable communities to drill down into practical details without cluttering the primary ballot question.</li>
              <li><strong>Independent Sub-Ballot Casting:</strong> Users can review and cast votes on individual sub-ballots according to their perspectives on each specific facet.</li>
            </ul>
          </div>

          {/* Section 4 */}
          <div className="legalSection">
            <span className="smallHeaderText">4. Ballot Creation & AI Assistance</span>
            <p className="smallText">
              Empowering grassroots community members to initiate constructive deliberation:
            </p>
            <ul className="legalList">
              <li><strong>User Ballot Creation:</strong> Registered users can propose new ballots by providing a clear question title, descriptive context, appropriate topic category, and distinct, balanced options.</li>
              <li><strong>AI-Assisted Formulation:</strong> Creators can utilize our built-in AI assistant to refine question clarity, generate balanced option sets, and validate options against ambiguity, duplication, or leading wording.</li>
              <li><strong>Category & Tag Classification:</strong> Ballots are categorized to allow community members to easily discover, filter, and participate in topics of interest.</li>
            </ul>
          </div>

          {/* Section 5 */}
          <div className="legalSection">
            <span className="smallHeaderText">5. Community-Specific Ballots</span>
            <p className="smallText">
              In addition to platform-wide public ballots, AmalgaMate empowers self-governing groups:
            </p>
            <ul className="legalList">
              <li><strong>Embedded Group Ballots:</strong> Dedicated communities on AmalgaMate can conduct community-specific ballots for their members to deliberate and decide on internal group topics and shared initiatives.</li>
              <li><strong>Community Guidelines:</strong> Community members participating in group ballots must adhere to both general platform rules and any community-specific codes of conduct.</li>
            </ul>
          </div>

          {/* Section 6 */}
          <div className="legalSection">
            <span className="smallHeaderText">6. Integrity, Anti-Abuse & Enforcement</span>
            <p className="smallText">
              Platform integrity and trustworthy consensus are strictly protected:
            </p>
            <ul className="legalList">
              <li><strong>Zero Tolerance for Sybils & Bots:</strong> Creating multiple accounts, deploying automated voting scripts, ballot flooding, or vote manipulation will result in immediate disqualification of votes and permanent account suspension.</li>
              <li><strong>Content Moderation:</strong> Comments or ballot descriptions containing abusive language, hate speech, spam, or promotional exploitation will be removed by moderation systems.</li>
              <li><strong>Appeals & Inquiries:</strong> Users subject to moderation or account review may submit an inquiry to our support team at <a href="mailto:support@amalgamate.ca" className="emailText">support@amalgamate.ca</a>.</li>
            </ul>
          </div>

          {/* Section 7 - Cross Navigation */}
          <div className="legalSection" style={{ borderBottom: "none" }}>
            <span className="smallHeaderText">7. Related Legal & Policy Documents</span>
            <p className="smallText">
              To learn more about your data protections and terms of service across AmalgaMate:
            </p>
            <ul className="legalList">
              <li>
                <strong>Privacy Policy:</strong> Learn how we safeguard your personal and account data —{" "}
                <span className="emailText" onClick={() => navigate("/privacy-policy")} style={{ cursor: "pointer" }}>
                  View Privacy Policy →
                </span>
              </li>
              <li>
                <strong>Terms of Use:</strong> Review our comprehensive user agreement, marketplace rules, and liability terms —{" "}
                <span className="emailText" onClick={() => navigate("/terms-of-use")} style={{ cursor: "pointer" }}>
                  View Terms of Use →
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VotingRule;
